import { API_URL } from '@/config';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Settings, Palette, Image as ImageIcon, Type, Save, Upload, MessageSquare, Phone, Mail, Globe, Link2, Facebook, Instagram, Twitter, Youtube, Bell, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';
import usePushNotifications from '@/hooks/usePushNotifications';

const API = `${API_URL}/api`;

const SettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [socialLinks, setSocialLinks] = useState({
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    youtube_url: '',
    linkedin_url: '',
    website_url: '',
    whatsapp_number: ''
  });
  const [whatsappConfig, setWhatsappConfig] = useState({
    provider: 'interakt',
    api_key: '',
    is_configured: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSection, setSavingSection] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pushStats, setPushStats] = useState(null);
  const [showPushDialog, setShowPushDialog] = useState(false);
  const [pushNotification, setPushNotification] = useState({ title: '', body: '', target: 'all' });
  const [sendingPush, setSendingPush] = useState(false);
  
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, toggleSubscription, isLoading: pushLoading } = usePushNotifications();

  useEffect(() => { loadSettings(); loadPushStats(); }, []);

  const loadPushStats = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await axios.get(`${API}/push/subscriptions`, { headers: { Authorization: `Bearer ${token}` } });
      setPushStats(response.data);
    } catch (error) {
      console.error('Failed to load push stats:', error);
    }
  };

  const handleSendPushNotification = async () => {
    if (!pushNotification.title || !pushNotification.body) {
      toast.error('Please fill in title and message');
      return;
    }
    
    setSendingPush(true);
    try {
      const token = localStorage.getItem("session_token");
      const response = await axios.post(`${API}/push/send`, pushNotification, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Push sent! ${response.data.sent}/${response.data.total} delivered`);
      setShowPushDialog(false);
      setPushNotification({ title: '', body: '', target: 'all' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send push notification');
    } finally {
      setSendingPush(false);
    }
  };

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [settingsRes, socialRes, whatsappRes] = await Promise.all([
        axios.get(`${API}/settings`, { headers }),
        axios.get(`${API}/settings/social-links`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/settings/whatsapp-config`, { headers }).catch(() => ({ data: {} }))
      ]);
      
      setSettings(settingsRes.data);
      setSocialLinks(prev => ({ ...prev, ...socialRes.data }));
      setWhatsappConfig(prev => ({ ...prev, ...whatsappRes.data }));
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSettings(prev => ({ ...prev, logo_url: response.data.url }));
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSocialLinks = async () => {
    setSavingSection('social');
    try {
      const token = localStorage.getItem("session_token");
      await axios.post(`${API}/settings/social-links`, socialLinks, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Social links saved!');
    } catch (error) {
      toast.error('Failed to save social links');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveWhatsappConfig = async () => {
    setSavingSection('whatsapp');
    try {
      const token = localStorage.getItem("session_token");
      await axios.post(`${API}/settings/whatsapp-config`, whatsappConfig, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('WhatsApp configuration saved!');
      loadSettings(); // Reload to get updated config status
    } catch (error) {
      toast.error('Failed to save WhatsApp configuration');
    } finally {
      setSavingSection(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-12 w-12 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Failed to load settings</p>
        <Button onClick={loadSettings} className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Club Settings</h2>
          <p className="text-muted-foreground">Manage your club branding and configuration</p>
        </div>
      </div>
      
      <form onSubmit={handleSave} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Type className="w-5 h-5" style={{ color: '#D4AF37' }} />
              Basic Information
            </CardTitle>
            <CardDescription>Club name and identity settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Club Name</Label>
              <Input 
                value={settings.club_name} 
                onChange={(e) => setSettings({...settings, club_name: e.target.value})} 
                placeholder="Enter club name"
                data-testid="club-name-input" 
              />
            </div>
            
            <div>
              <Label>Notification Footer</Label>
              <Input 
                value={settings.notification_footer} 
                onChange={(e) => setSettings({...settings, notification_footer: e.target.value})} 
                placeholder="Footer text for notifications"
                data-testid="notif-footer-input" 
              />
              <p className="text-xs text-muted-foreground mt-1">This text appears at the bottom of all notifications sent</p>
            </div>
          </CardContent>
        </Card>
        
        {/* Logo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" style={{ color: '#D4AF37' }} />
              Club Logo
            </CardTitle>
            <CardDescription>Upload your club logo</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-6">
              <div className="w-32 h-32 rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden" style={{ borderColor: '#E2E8F0' }}>
                {settings.logo_url ? (
                  <img 
                    src={getImageUrl(settings.logo_url)}
                    alt="Club Logo" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <ImageIcon className="w-12 h-12 text-muted-foreground" />
                )}
              </div>
              
              <div className="flex-1 space-y-3">
                <div>
                  <Label>Upload Logo</Label>
                  <Input 
                    type="file" 
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploading}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Recommended: 512x512px, PNG or JPG</p>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Or enter URL directly:</span>
                </div>
                <Input 
                  value={settings.logo_url || ''} 
                  onChange={(e) => setSettings({...settings, logo_url: e.target.value})} 
                  placeholder="https://example.com/logo.png"
                  data-testid="logo-url-input" 
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Theme Colors */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" style={{ color: '#D4AF37' }} />
              Theme Colors
            </CardTitle>
            <CardDescription>Customize your club color scheme</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Primary Color</Label>
                <div className="flex items-center gap-3 mt-2">
                  <div 
                    className="w-12 h-12 rounded-lg border shadow-sm cursor-pointer"
                    style={{ backgroundColor: settings.primary_color }}
                    onClick={() => document.getElementById('primary-color-picker').click()}
                  />
                  <div className="flex-1">
                    <Input 
                      value={settings.primary_color} 
                      onChange={(e) => setSettings({...settings, primary_color: e.target.value})} 
                      placeholder="#0F172A"
                      data-testid="primary-color-input" 
                    />
                    <input 
                      type="color" 
                      id="primary-color-picker"
                      value={settings.primary_color}
                      onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                      className="sr-only"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Deep Royal Navy</p>
              </div>
              
              <div>
                <Label>Secondary Color</Label>
                <div className="flex items-center gap-3 mt-2">
                  <div 
                    className="w-12 h-12 rounded-lg border shadow-sm cursor-pointer"
                    style={{ backgroundColor: settings.secondary_color }}
                    onClick={() => document.getElementById('secondary-color-picker').click()}
                  />
                  <div className="flex-1">
                    <Input 
                      value={settings.secondary_color} 
                      onChange={(e) => setSettings({...settings, secondary_color: e.target.value})} 
                      placeholder="#D4AF37"
                      data-testid="secondary-color-input" 
                    />
                    <input 
                      type="color" 
                      id="secondary-color-picker"
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                      className="sr-only"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Metallic Gold</p>
              </div>
            </div>
            
            {/* Color Preview */}
            <Separator />
            <div>
              <Label className="text-muted-foreground">Preview</Label>
              <div className="mt-3 p-4 rounded-lg" style={{ backgroundColor: settings.primary_color }}>
                <p className="font-bold mb-2" style={{ color: settings.secondary_color }}>
                  {settings.club_name}
                </p>
                <Button 
                  type="button" 
                  size="sm"
                  style={{ backgroundColor: settings.secondary_color, color: settings.primary_color }}
                >
                  Sample Button
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Save Basic Settings Button */}
        <div className="flex justify-end">
          <Button 
            type="submit" 
            disabled={saving || uploading}
            style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
            data-testid="save-settings-btn"
            className="min-w-[140px]"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Social Links Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" style={{ color: '#D4AF37' }} />
            Social Links
          </CardTitle>
          <CardDescription>Configure social media and website links</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Website URL
              </Label>
              <Input
                value={socialLinks.website_url}
                onChange={(e) => setSocialLinks({ ...socialLinks, website_url: e.target.value })}
                placeholder="https://yourwebsite.com"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                WhatsApp Number
              </Label>
              <Input
                value={socialLinks.whatsapp_number}
                onChange={(e) => setSocialLinks({ ...socialLinks, whatsapp_number: e.target.value })}
                placeholder="+919999999999"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Facebook className="w-4 h-4" />
                Facebook
              </Label>
              <Input
                value={socialLinks.facebook_url}
                onChange={(e) => setSocialLinks({ ...socialLinks, facebook_url: e.target.value })}
                placeholder="https://facebook.com/yourpage"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Instagram className="w-4 h-4" />
                Instagram
              </Label>
              <Input
                value={socialLinks.instagram_url}
                onChange={(e) => setSocialLinks({ ...socialLinks, instagram_url: e.target.value })}
                placeholder="https://instagram.com/yourhandle"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Twitter className="w-4 h-4" />
                Twitter / X
              </Label>
              <Input
                value={socialLinks.twitter_url}
                onChange={(e) => setSocialLinks({ ...socialLinks, twitter_url: e.target.value })}
                placeholder="https://twitter.com/yourhandle"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Youtube className="w-4 h-4" />
                YouTube
              </Label>
              <Input
                value={socialLinks.youtube_url}
                onChange={(e) => setSocialLinks({ ...socialLinks, youtube_url: e.target.value })}
                placeholder="https://youtube.com/@yourchannel"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleSaveSocialLinks}
              disabled={savingSection === 'social'}
              style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
            >
              {savingSection === 'social' ? 'Saving...' : 'Save Social Links'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Integrations Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" style={{ color: '#D4AF37' }} />
            Integrations
          </CardTitle>
          <CardDescription>Configure third-party services for notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* WhatsApp Integration */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium">WhatsApp (Interakt)</h4>
                  <p className="text-sm text-muted-foreground">Send OTPs and notifications via WhatsApp</p>
                </div>
              </div>
              <Badge className={whatsappConfig.is_configured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                {whatsappConfig.is_configured ? 'Configured' : 'Not Configured'}
              </Badge>
            </div>
            <div className="space-y-3">
              <div>
                <Label>Interakt API Key</Label>
                <Input
                  type="password"
                  value={whatsappConfig.api_key}
                  onChange={(e) => setWhatsappConfig({ ...whatsappConfig, api_key: e.target.value })}
                  placeholder="Enter your Interakt API key"
                />
                {whatsappConfig.api_key_masked && (
                  <p className="text-xs text-muted-foreground mt-1">Current key: {whatsappConfig.api_key_masked}</p>
                )}
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={handleSaveWhatsappConfig}
                  disabled={savingSection === 'whatsapp'}
                  variant="outline"
                >
                  {savingSection === 'whatsapp' ? 'Saving...' : 'Save WhatsApp Config'}
                </Button>
              </div>
            </div>
          </div>

          {/* Email Status */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium">Email (SMTP)</h4>
                  <p className="text-sm text-muted-foreground">Gmail SMTP for email notifications</p>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-800">Configured</Badge>
            </div>
          </div>

          {/* SMS Status */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium">SMS (Twilio)</h4>
                  <p className="text-sm text-muted-foreground">Twilio for SMS notifications</p>
                </div>
              </div>
              <Badge className="bg-yellow-100 text-yellow-800">Trial Account</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              SMS only works for verified numbers. Upgrade Twilio account for full access.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Push Notifications Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" style={{ color: '#D4AF37' }} />
            Push Notifications
          </CardTitle>
          <CardDescription>Send instant notifications to subscribed users</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold" style={{ color: '#D4AF37' }}>
                {pushStats?.total || 0}
              </div>
              <div className="text-xs text-muted-foreground">Total Subscribers</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold" style={{ color: '#3B82F6' }}>
                {pushStats?.with_user_account || 0}
              </div>
              <div className="text-xs text-muted-foreground">Registered Users</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold" style={{ color: '#8B5CF6' }}>
                {pushStats?.admin_users || 0}
              </div>
              <div className="text-xs text-muted-foreground">Admin Users</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold" style={{ color: '#10B981' }}>
                {pushStats?.anonymous || 0}
              </div>
              <div className="text-xs text-muted-foreground">Anonymous</div>
            </div>
          </div>

          {/* Your subscription */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">Your Subscription</h4>
                <p className="text-sm text-muted-foreground">
                  {pushSupported 
                    ? (pushSubscribed ? 'You are subscribed to receive push notifications' : 'Subscribe to receive push notifications')
                    : 'Push notifications are not supported in this browser'
                  }
                </p>
              </div>
              <Button
                variant={pushSubscribed ? "outline" : "default"}
                onClick={toggleSubscription}
                disabled={!pushSupported || pushLoading}
                style={!pushSubscribed ? { backgroundColor: '#D4AF37', color: '#0F172A' } : {}}
              >
                {pushLoading ? 'Loading...' : (pushSubscribed ? 'Unsubscribe' : 'Subscribe')}
              </Button>
            </div>
          </div>

          {/* Send Notification */}
          <div className="flex justify-end">
            <Button
              onClick={() => setShowPushDialog(true)}
              style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
            >
              <Send className="w-4 h-4 mr-2" />
              Send Push Notification
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Push Notification Dialog */}
      <Dialog open={showPushDialog} onOpenChange={setShowPushDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Push Notification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title</Label>
              <Input
                value={pushNotification.title}
                onChange={(e) => setPushNotification({ ...pushNotification, title: e.target.value })}
                placeholder="Notification title"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={pushNotification.body}
                onChange={(e) => setPushNotification({ ...pushNotification, body: e.target.value })}
                placeholder="Notification message"
                rows={3}
              />
            </div>
            <div>
              <Label>Target Audience</Label>
              <select
                value={pushNotification.target}
                onChange={(e) => setPushNotification({ ...pushNotification, target: e.target.value })}
                className="w-full mt-1 p-2 border rounded-md"
              >
                <option value="all">All Subscribers ({pushStats?.total || 0})</option>
                <option value="members">Registered Users ({pushStats?.with_user_account || 0})</option>
                <option value="admins">Admin Users ({pushStats?.admin_users || 0})</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPushDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSendPushNotification}
              disabled={sendingPush}
              style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
            >
              {sendingPush ? 'Sending...' : 'Send Notification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
